import { clsx } from "clsx";

import { useUIConfig } from "../../../config/uiConfigContext";

import styles from "./RVPaddedPage.module.scss";

/**
 * Props for the RVPaddedPage component.
 */
type RVPaddedPageProps = {
    /** Additional CSS class name to apply to the page container */
    className?: string;
    /**
     * If true, removes default padding from the page.
     * Useful for pages that need to control their own spacing.
     * @default false
     */
    noPadding?: boolean;
    /** Additional props to spread onto the container div */
    [x: string]: any;
};

/**
 * A page layout wrapper component that provides consistent padding across the application.
 *
 * This component handles:
 * - Standard page padding for non-form pages
 * - Responsive adjustments when embedded in an iframe
 * - Optional removal of padding for custom layouts
 * - Proper positioning for pages containing Grid components
 *
 * When the page contains a Grid or LoadingGrid as the first child, it uses absolute positioning
 * to ensure the page width is determined by the viewport rather than content, while still
 * respecting flexbox layout.
 *
 * @example
 * ```tsx
 * // Standard usage
 * <RVPaddedPage>
 *   <div>Page content</div>
 * </RVPaddedPage>
 *
 * // Without padding
 * <RVPaddedPage noPadding>
 *   <CustomLayout />
 * </RVPaddedPage>
 *
 * // With custom class
 * <RVPaddedPage className="custom-page">
 *   <div>Content</div>
 * </RVPaddedPage>
 * ```
 *
 * @see RVFormPageLayout - Use for pages containing forms
 */
export function RVPaddedPage({
    children,
    className = "",
    noPadding = false,
    ...props
}: React.PropsWithChildren<RVPaddedPageProps>): JSX.Element {
    const { isIframed } = useUIConfig();

    const isAppEmbedded = isIframed();
    // if the padded page contains a <Grid /> or a <LoadingGrid /> as the first child
    // we want to absolutely position the page because we don't want the page
    // to have the width dictated by its contents, but instead by the viewport
    // this page is displayed in a flexbox, so by positioning it absolutely
    // it will still respect the flexbox, it will simply not grow past the viewport
    return (
        <div
            className={clsx(
                styles.padding,
                { [styles.noPadding]: noPadding, [styles.embedded]: isAppEmbedded },
                className
            )}
            {...props}
        >
            {children}
        </div>
    );
}
